import { Navigate } from 'react-router-dom'
import ProtectedRoute from '@/components/routes/ProtectedRoute'
import Dashboard from '@/pages/Dashboard'
import Profile from '@/pages/Profile'
import PayOnline from '@/pages/PayOnline'
import Products from '@/pages/Products'
import ProductLandingPage from '@/pages/ProductLandingPage'
import DeliveryManagement from '@/pages/DeliveryManagement'
import PauseDeliveries from '@/pages/PauseDeliveries'
import LoginPage from '@/pages/auth/LoginPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import AcceptInvitePage from '@/pages/auth/AcceptInvitePage'
import MagicLoginPage from '@/pages/auth/MagicLoginPage'
import StartReset from '@/pages/StartReset'
import CrmDeliveriesPage from '@/pages/CrmDeliveriesPage'
import NotFound from '@/pages/errors/NotFound'
import Unauthorized from '@/pages/errors/Unauthorized'
import type { RouteConfig } from '@/types'
import ContactUs from '@/pages/ContactUs'
import TermsOfServices from '@/pages/TermsOfServices'
import PrivacyPolicy from '@/pages/PrivacyPolicy'

/**
 * Routes that don't require authentication
 */
export const authRoutes: RouteConfig[] = [
  {
    path: '/login',
    element: <LoginPage />,
    name: 'Login',
    title: 'Sign in',
    // DF-338: exact SEO title for the login page (the public entry point).
    documentTitle: 'Log In to Your Customer Portal | McQueens Dairies',
  },
  {
    // FEDEV-528: registration is folded into the unified login flow, so the
    // standalone page is gone and the old URL redirects to it.
    path: '/register',
    element: <Navigate to="/login" replace />,
    name: 'Register',
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
    name: 'Forgot Password',
    title: 'Forgotten password',
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
    name: 'Reset Password',
    title: 'Reset your password',
  },
  {
    path: '/accept-invite',
    element: <AcceptInvitePage />,
    name: 'Accept Invite',
    title: 'Accept your invitation',
  },
  {
    path: '/magic-login',
    element: <MagicLoginPage />,
    name: 'Magic Login',
    title: 'Signing you in',
  },
  {
    // Support-facing "reset" entry point: clears client state and
    // hard-reloads onto the latest build, then drops the user at login.
    path: '/start',
    element: <StartReset />,
    name: 'Start',
    title: 'Starting again',
  },
  {
    // DF-345: read-only delivery view embedded in Dynamics via an iframe.
    // Interim "simple URL" version: ?contact={crm contact guid}, no staff
    // sign-in yet. Renders bare (no header/footer, see App.tsx).
    path: '/crm/deliveries',
    element: <CrmDeliveriesPage />,
    name: 'CRM Deliveries',
    title: 'Deliveries',
  },
  {
    path: '/contact-us',
    element: <ContactUs />,
    name: 'Contact Us',
    title: 'Customer Services',
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
    name: 'Privacy Policy',
    title: 'Privacy Policy',
  },
  {
    path: '/terms-of-services',
    element: <TermsOfServices />,
    name: 'Terms of Services',
    title: 'Terms and Conditions',
  },
];

/**
 * Protected Routes
 */
export const protectedRoutes: RouteConfig[] = [
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
    name: 'Dashboard',
    title: 'My Deliveries',
    // DF-338: exact SEO title for the portal homepage (the signed-in dashboard
    // that '/' resolves to).
    documentTitle: 'Customer Portal | Manage Your Milk Deliveries | McQueens Dairies',
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    ),
    name: 'Profile',
    title: 'Your details',
  },
  {
    // The old standing-order page is retired (DF-392): nothing linked to it and
    // My Deliveries shows the same detail. Old links land on My Deliveries.
    path: '/weekly-order',
    element: <Navigate to="/" replace />,
    name: 'Weekly Order',
  },
  {
    // Laura's Delivery Management page: deliveries by day, paused deliveries,
    // address and driver instructions. Separate from /weekly-order by design.
    path: '/delivery-management',
    element: (
      <ProtectedRoute>
        <DeliveryManagement />
      </ProtectedRoute>
    ),
    name: 'Delivery Management',
    title: 'Delivery Management',
  },
  {
    // Pause deliveries as a full page, for the mobile bottom nav. The desktop
    // rail still opens the same calendar as a dialog.
    path: '/pause-deliveries',
    element: (
      <ProtectedRoute>
        <PauseDeliveries />
      </ProtectedRoute>
    ),
    name: 'Pause deliveries',
    title: 'Pause deliveries',
  },
  {
    path: '/products',
    element: (
      <ProtectedRoute>
        <Products />
      </ProtectedRoute>
    ),
    name: 'Products',
    title: 'Products',
  },
  {
    // A single product's landing page, reached from a campaign email (DF-230).
    // The handle is `{sku}-{slug}`; the page resolves on the leading SKU and
    // ignores the slug tail. Protected, so a logged-out visitor is trapped to
    // login and returned here afterwards (see App.tsx ProtectedRoute).
    path: '/products/:handle',
    element: (
      <ProtectedRoute>
        <ProductLandingPage />
      </ProtectedRoute>
    ),
    name: 'Product',
    title: 'Product',
  },
  {
    path: '/pay-online',
    element: (
      <ProtectedRoute>
        <PayOnline />
      </ProtectedRoute>
    ),
    name: 'Payments',
    title: 'Payments',
  },
]

/**
 * Error Routes
 */
export const errorRoutes: RouteConfig[] = [
  {
    path: '/unauthorized',
    element: <Unauthorized />,
    name: '401 Unauthorized',
    title: 'Not authorised',
  },
  {
    path: '*',
    element: <NotFound />,
    name: '404 Not Found',
    title: 'Page not found',
  },
]

export const routes = {
  auth: authRoutes,
  protected: protectedRoutes,
  error: errorRoutes,
}

export default routes
